import { call, put, takeLatest, select } from "redux-saga/effects";

import { getCoupon, validateCoupon } from "./CartApis";
import * as ACTIONS from "./CartActions";
import * as TYPES from "./CartTypes";
import { setErrorMessage } from "../Error/ErrorAction";
import {
  ALL_DELIVERY_TYPES,
  COUPONS,
  CURRENCY,
  ORDER_TYPES,
  UNSUPPORTED_COUPON_TYPES,
} from "../../utils/Constants";

// Replace with your sagas
export function* sagasCouponRequest({ payload }) {
  try {
    const hotelKey = payload;
    if (!hotelKey) {
      throw new Error("Something went wrong with the hotel key");
    }

    const response = yield call(getCoupon, hotelKey);
    if (!response?.data?.data) {
      throw new Error("Something went wrong with the response");
    }

    yield put(ACTIONS.getCouponReceive(response.data.data));
  } catch (err) {
    yield put(ACTIONS.getCouponReceive([]));
  }
}

export function* sagasValidateCoupon({ payload }) {
  yield put(ACTIONS.IsValidating(true));
  // Start every validation run with the result reset to false. The flag is
  // only flipped to true once every rule check passes, so the React side
  // (CouponList useEffect) can't accidentally apply a coupon that failed
  // a downstream check (order type, min order, max order).
  yield put(ACTIONS.setCouponValidationResult(false));

  try {
    const { customerId, code, store_id, time } = payload;
    const response = yield call(validateCoupon, customerId, code, store_id, time);
    yield put(ACTIONS.IsValidating(false));

    const coupon = response?.data?.data;
    if (response?.data?.status !== true || !coupon?.id) {
      yield put(
        setErrorMessage({
          message: response?.data?.message || "Invalid Coupon",
          showAs: "dialog",
        })
      );
      return;
    }

    // Reject coupon types whose handling isn't implemented in the app yet
    // (e.g. BUY_N_GET_M_FREE). Without this guard a user could type the code
    // and end up with a coupon that silently fails to apply discounts.
    const couponType = coupon?.result?.type || coupon?.rules?.type;
    if (UNSUPPORTED_COUPON_TYPES.includes(couponType)) {
      yield put(
        setErrorMessage({
          message: "This coupon is not supported in the app yet",
          showAs: "dialog",
        })
      );
      return;
    }

    const { priceSummary, checkoutDetails, data, coupons: couponList } =
      yield select((state) => state.cart);

    // Item-discount applicability (e.g. free single-item coupon when only
    // one matching item with count > 1 is in the cart).
    const checkItemDiscountApplicability = (c) => {
      if (
        c?.rules?.type === COUPONS.ITEM_DISCOUNT &&
        c?.items &&
        c?.items?.length > 0
      ) {
        const applicableItems = JSON.parse(c?.items) || [];
        const validItemsInCart = data?.filter((item) =>
          applicableItems.includes(item.itemId)
        );
        if (
          applicableItems?.length === 1 &&
          c?.result?.DISCOUNT === 100 &&
          validItemsInCart?.length === 1 &&
          validItemsInCart[0]?.count > 1
        ) {
          return false;
        }
      }
      return true;
    };

    if (!checkItemDiscountApplicability(coupon)) {
      yield put(
        setErrorMessage({
          message: response?.data?.message || "Invalid Coupon",
          showAs: "dialog",
        })
      );
      return;
    }

    // Order-type check — must match cart's order type or be ALL.
    const orderTypeOk =
      coupon?.rules?.APPLICABLE_FOR === checkoutDetails?.orderType ||
      coupon?.rules?.APPLICABLE_FOR === ALL_DELIVERY_TYPES;
    if (!orderTypeOk) {
      yield put(
        setErrorMessage({
          message: `Only applicable for ${
            coupon?.rules?.APPLICABLE_FOR === ORDER_TYPES.DELIVERY
              ? "Delivery"
              : "Pickup"
          }`,
          showAs: "dialog",
        })
      );
      return;
    }

    // Min / max order-value checks — mirror the list-side filter in
    // Coupons.findApplicableCoupons so both entry points behave the same.
    const subTotal = Number(priceSummary?.subTotal);
    const minOrderValue = Number(coupon?.rules?.MIN_ORDER_VALUE);
    const maxOrderValue = Number(coupon?.rules?.MAX_ORDER_VALUE);

    if (!Number.isNaN(minOrderValue) && !(subTotal > minOrderValue)) {
      yield put(
        setErrorMessage({
          message: `Order value should be more than ${CURRENCY.SIGN}${coupon?.rules?.MIN_ORDER_VALUE}`,
          showAs: "dialog",
        })
      );
      return;
    }

    if (
      !Number.isNaN(maxOrderValue) &&
      maxOrderValue > 0 &&
      !(subTotal < maxOrderValue)
    ) {
      yield put(
        setErrorMessage({
          message: `Order value should be less than ${CURRENCY.SIGN}${coupon?.rules?.MAX_ORDER_VALUE}`,
          showAs: "dialog",
        })
      );
      return;
    }

    // BUY_ONE_GET_ONE_FREE precondition: cart must contain at least one item
    // matching the coupon's APPLICABLE_ITEMS list (id + optional size). If
    // sizes is empty the rule matches any size of that item id.
    if (couponType === COUPONS.BUY_ONE_GET_ONE_FREE) {
      const applicableItems = coupon?.rules?.APPLICABLE_ITEMS || [];
      const hasEligibleCartItem =
        Array.isArray(applicableItems) &&
        applicableItems.length > 0 &&
        (data || []).some((cartItem) =>
          applicableItems.some((ai) => {
            if (Number(ai?.id) !== Number(cartItem?.itemId)) return false;
            if (Array.isArray(ai?.sizes) && ai.sizes.length > 0) {
              const cartSizeId =
                cartItem?.size?.id ?? cartItem?.size ?? null;
              return ai.sizes.some(
                (s) => Number(s) === Number(cartSizeId)
              );
            }
            return true;
          })
        );
      if (!hasEligibleCartItem) {
        yield put(
          setErrorMessage({
            message:
              "Add an applicable product to your cart to use this coupon",
            showAs: "dialog",
          })
        );
        return;
      }
    }

    // For BUY_ONE_GET_ONE_FREE the validate-coupon API returns a thin payload
    // (no `applicable_products`, no `parseditems`) so the BOGO downstream
    // logic can't add the free item even though the coupon is "applied".
    // Fall back to the matching record from the get-coupon list (which has
    // the full product data) when available. Other coupon types stay on the
    // validate response — their flow is unaffected.
    let finalCoupon = coupon;
    if (couponType === COUPONS.BUY_ONE_GET_ONE_FREE) {
      const fullCoupon = (couponList || []).find((c) => c?.id === coupon?.id);
      if (fullCoupon) {
        finalCoupon = { ...coupon, ...fullCoupon };
      }
    }

    // All checks passed — commit the validated coupon and flip the flag.
    yield put(ACTIONS.setValidatedCoupon(finalCoupon));
    yield put(ACTIONS.setCouponValidationResult(true));
    if (payload?.isNewCoupon) {
      yield put(ACTIONS.getCouponReceive([finalCoupon]));
    }
  } catch (err) {
    console.error("Error during coupon validation:", err);
    yield put(ACTIONS.IsValidating(false));
    yield put(ACTIONS.setCouponValidationResult(false));
    yield put(
      setErrorMessage({
        message: "Something went wrong",
        showAs: "dialog",
      })
    );
  }
}
// export function* sagasValidateCoupon({ state, payload }) {
//   try {
//     const { hotelId, code } = payload;
//     if (!hotelId) {
//       throw new Error("Something went wrong with the hotel id");
//     }

//     const response = yield call(validateCoupon, hotelId, code);
//     const coupon = response?.data?.data;
//     //checking coupon is applicabile
//     if (coupon?.id) {
//       const { priceSummary, checkoutDetails, data } = yield select(
//         (state) => state.cart
//       );

//       //   const getPrice = (coupon) => {
//       //     if (coupon?.rules?.type === COUPONS.ITEM_DISCOUNT) {
//       //       const applicableItems = JSON.parse(coupon?.items) || [];
//       //       const validItemsInCart = data?.filter((item) =>
//       //         applicableItems.includes(item.itemId)
//       //       );
//       //       let itemTotal = 0;
//       //       validItemsInCart.forEach((item) => {
//       //         const itemPrice = item?.count * item?.sale_price;
//       //         itemTotal = itemTotal + itemPrice;
//       //       });
//       //       return Number(itemTotal);
//       //     } else {
//       //       return Number(priceSummary?.subTotal);
//       //     }
//       //   };

//       const checkItemDiscountApplicability = (coupon) => {
//         if (
//           coupon?.rules?.type === COUPONS.ITEM_DISCOUNT &&
//           coupon?.items &&
//           coupon?.items?.length > 2
//         ) {
//           const applicableItems = JSON.parse(coupon?.items) || [];
//           const validItemsInCart = data?.filter((item) =>
//             applicableItems.includes(item.itemId)
//           );
//           // This condition for: 100% discount item can have only 1 quantity of that particular item
//           if (
//             applicableItems?.length === 1 &&
//             coupon?.result?.DISCOUNT === 100 &&
//             validItemsInCart?.length === 1 &&
//             validItemsInCart[0]?.count > 1
//           ) {
//             return false;
//           } else return !!validItemsInCart.length;
//         } else return true;
//       };

//       //check if it is exposed coupon or not
//       if (checkItemDiscountApplicability(coupon)) {
//         // check greater than subtotal
//         if (
//           coupon?.rules?.APPLICABLE_FOR === checkoutDetails?.orderType ||
//           coupon?.rules?.APPLICABLE_FOR === ALL_DELIVERY_TYPES
//         ) {
//           //   check delivery type
//           if (
//             Number(priceSummary?.subTotal) >
//             Number(coupon?.rules?.MIN_ORDER_VALUE)
//           ) {
//             //if coupon is applicable
//             yield put(ACTIONS.selectCoupon(coupon));
//           } else {
//             //if coupon is not applicable
//             yield put(
//               setErrorMessage({
//                 message: `Order value should be more than ${CURRENCY.SIGN}${coupon?.rules?.MIN_ORDER_VALUE}`,
//                 showAs: "dialog",
//               })
//             );
//           }
//         } else {
//           //if coupon is not applicable
//           yield put(
//             setErrorMessage({
//               message: `Only applicable for ${
//                 coupon?.rules?.APPLICABLE_FOR === COUPONS.CART_DISCOUNT
//                   ? "cart discount"
//                   : "item discount"
//               }`,
//               showAs: "dialog",
//             })
//           );
//         }
//       } else {
//         //if coupon is not applicable
//         yield put(
//           setErrorMessage({
//             message: `Invalid coupon`,
//             showAs: "dialog",
//           })
//         );
//       }

//       //   if (
//       //     Number(priceSummary?.subTotal) >
//       //       Number(coupon?.rules?.MIN_ORDER_VALUE) &&
//       //     (coupon?.rules?.APPLICABLE_FOR === checkoutDetails?.orderType ||
//       //       coupon?.rules?.APPLICABLE_FOR === ALL_DELIVERY_TYPES)
//       //   ) {
//       //     //if coupon is applicable
//       //     yield put(ACTIONS.selectCoupon(coupon));
//       //   } else {
//       //     //if coupon is not applicable
//       //     yield put(
//       //       setErrorMessage({
//       //         message: `Minimum order value is atleast $${coupon?.rules?.MIN_ORDER_VALUE}`,
//       //         showAs: "dialog",
//       //       })
//       //     );
//       //   }
//     } else {
//       yield put(
//         setErrorMessage({
//           message: "Invalid coupon",
//           showAs: "dialog",
//         })
//       );
//     }
//   } catch (err) {
//     yield put(
//       setErrorMessage({
//         message: "Something went wrong",
//         showAs: "dialog",
//       })
//     );
//   }
// }

export function* CartSaga() {
  yield takeLatest(TYPES.GET_COUPON_REQUEST, sagasCouponRequest);
}

export function* VerifyCouponSaga() {
  yield takeLatest(TYPES.VALIDATE_COUPON, sagasValidateCoupon);
}
